import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/dtos/Enums';

/**
 * Hook to check user permissions based on roles
 */
export function usePermissions() {
  const { user } = useAuth();

  /**
   * Check if user has any of the specified roles (case-insensitive)
   */
  const hasRole = (roles: string[]): boolean => {
    if (!user?.roles || user.roles.length === 0) return false;
    
    const userRolesUpper = user.roles.map(r => r.toUpperCase());
    return roles.some(role => {
      const roleUpper = role.toUpperCase();
      return userRolesUpper.includes(roleUpper);
    });
  };

  /**
   * Check if user is Admin
   */
  const isAdmin = (): boolean => {
    return hasRole(['Admin']);
  };

  /**
   * Check if user is Medic
   */
  const isMedic = (): boolean => {
    return hasRole(['Medic']);
  };

  /**
   * Check if user is Student
   */
  const isStudent = (): boolean => {
    return hasRole(['Student']);
  };

  /**
   * Check if user is Admin or Medic
   */
  const isAdminOrMedic = (): boolean => {
    return hasRole(['Admin', 'Medic']);
  };

  /**
   * Check if user can create (Admin or Medic)
   */
  const canCreate = (): boolean => {
    return isAdminOrMedic();
  };

  /**
   * Check if user can update (Admin or Medic)
   */
  const canUpdate = (): boolean => {
    return isAdminOrMedic();
  };

  /**
   * Check if user can delete (Admin or Medic)
   */
  const canDelete = (): boolean => {
    return isAdminOrMedic();
  };

  /**
   * Check if user can read (any authenticated user)
   */
  const canRead = (): boolean => {
    return !!user;
  };

  return {
    hasRole,
    isAdmin,
    isMedic,
    isStudent,
    isAdminOrMedic,
    canCreate,
    canUpdate,
    canDelete,
    canRead,
  };
}
